import {
  BulkFieldEditDialog,
  type BulkFieldSpec,
  type BulkResult,
} from '@/components/common/BulkFieldEditDialog';
import { inventoryApi, type BulkInventoryChanges } from '@/api/services/inventory.api';

/**
 * Which inventory fields a bulk edit may set.
 *
 * Deliberately narrower than the update DTO: `sku` and `name` identify an item,
 * so setting either across a selection would collide on the unique SKU or give
 * every selected item the same name. Neither is what anyone means by "edit 40
 * items at once".
 */
const FIELDS: BulkFieldSpec[] = [
  { key: 'category', label: 'Category', type: 'text' },
  {
    key: 'abcClassification',
    label: 'ABC class',
    type: 'select',
    options: [
      { value: 'A', label: 'A — high value' },
      { value: 'B', label: 'B — medium value' },
      { value: 'C', label: 'C — low value' },
    ],
  },
  { key: 'minStockLevel', label: 'Min stock level', type: 'number' },
  { key: 'maxStockLevel', label: 'Max stock level', type: 'number' },
  {
    key: 'reorderPoint',
    label: 'Reorder point',
    type: 'number',
    hint: 'Must not exceed max stock',
  },
  { key: 'leadTimeDays', label: 'Lead time (days)', type: 'number' },
  { key: 'safetyStockDays', label: 'Safety stock (days)', type: 'number' },
  {
    key: 'demandVariability',
    label: 'Demand variability',
    type: 'select',
    options: [
      { value: 'LOW', label: 'Low' },
      { value: 'MEDIUM', label: 'Medium' },
      { value: 'HIGH', label: 'High' },
    ],
  },
  {
    key: 'isActive',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ],
    parse: (raw) => raw === 'true',
  },
];

interface BulkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemIds: string[];
  onApplied: (result: BulkResult) => void;
}

export function BulkEditDialog({ open, onOpenChange, itemIds, onApplied }: BulkEditDialogProps) {
  return (
    <BulkFieldEditDialog
      open={open}
      onOpenChange={onOpenChange}
      ids={itemIds}
      fields={FIELDS}
      noun="item"
      submit={(ids, changes) =>
        inventoryApi.bulkUpdate({ itemIds: ids, changes: changes as BulkInventoryChanges })
      }
      describeRow={(row) => ({
        title: (row.sku as string) ?? row.id,
        subtitle: row.name as string | undefined,
      })}
      onApplied={onApplied}
    />
  );
}
